import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

const AuthPage = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showUserAgreement, setShowUserAgreement] = useState(false);
  const [pendingRegistration, setPendingRegistration] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // For registration, show User Agreement first
    if (!isLogin) {
      // Validate inputs before showing agreement
      if (!username.trim() || !password.trim()) {
        toast.error('Please enter username and password');
        return;
      }
      // Store credentials and show agreement
      setPendingRegistration({ username: username.trim(), password });
      setShowUserAgreement(true);
      return;
    }
    
    // For login, proceed normally
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password,
      });
      onLogin(response.data.access_token, response.data.user);
    } catch (error) {
      // Error hidden - silent fail
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptAgreement = async () => {
    if (!pendingRegistration) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        username: pendingRegistration.username,
        password: pendingRegistration.password,
      });
      
      // Mark agreement as accepted immediately after account creation
      const token = response.data.access_token;
      await axios.post(
        `${API}/account/accept-agreement`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Account created successfully!');
      setShowUserAgreement(false);
      setPendingRegistration(null);
      onLogin(token, response.data.user);
    } catch (error) {
      toast.error('Registration failed. Username may be taken.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeclineAgreement = () => {
    setShowUserAgreement(false);
    setPendingRegistration(null);
    setUsername('');
    setPassword('');
    toast.info('Registration cancelled');
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-2 relative overflow-hidden"
      style={{
        backgroundImage: 'url(/lockscreen-background.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Dark overlay for better contrast */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm"></div>

      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl top-20 -left-20 animate-pulse-slow"></div>
        <div className="absolute w-96 h-96 bg-orange-500/10 rounded-full blur-3xl bottom-20 -right-20 animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="relative z-10 w-full max-w-2xl px-2">
        <div 
          className="beypointer-container p-6 md:p-8 relative overflow-hidden"
          style={{
            backgroundImage: 'url(/login-background.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          {/* Dark overlay for device screen effect */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm rounded-[29px]"></div>
          
          {/* Header */}
          <div className="text-center mb-8 relative z-10">
            <h1 className="text-4xl font-bold digital-font text-cyan-400 glow-cyan mb-2">
              BEYPOINTER
            </h1>
            <p className="text-slate-400 text-sm">Battle Points Exchange System</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-slate-300 font-medium">
                Username
              </Label>
              <Input
                id="username"
                data-testid="auth-username-input"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                spellCheck={false}
                autoComplete="off"
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 focus:ring-cyan-500/20"
                placeholder="Enter your username"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300 font-medium">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="auth-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 focus:ring-cyan-500/20 pr-10"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-cyan-400 transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="auth-submit-button"
              disabled={loading}
              className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white font-semibold py-6 text-lg shadow-lg hover:shadow-cyan-500/20 transition-all duration-300"
            >
              {loading ? 'Processing...' : isLogin ? 'LOGIN' : 'REGISTER'}
            </Button>
          </form>

          {/* Toggle between login/register */}
          <div className="mt-6 text-center relative z-10">
            <button
              type="button"
              data-testid="auth-toggle-button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-cyan-400 hover:text-cyan-300 text-sm transition-colors"
            >
              {isLogin ? "Don't have an account? Register" : 'Already have an account? Login'}
            </button>
          </div>
        </div>
      </div>

      {/* User Agreement Modal */}
      <Dialog open={showUserAgreement} onOpenChange={() => {}}>
        <DialogContent className="bg-slate-900 border-cyan-500/50 text-white max-w-2xl max-h-[85vh] overflow-hidden flex flex-col [&>button]:hidden" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-cyan-400 font-bold text-xl text-center">
              User Agreement
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-2 text-sm text-slate-300 space-y-4 max-h-[50vh]">
            <h2 className="text-lg font-bold text-white">BBXD/Beypointer.com User Agreement</h2>
            <p className="text-slate-400">
              This User Agreement ("Agreement") is a legal contract between you ("User," "you") and <strong className="text-white">BBXD</strong> ("BBXD," "we," "us," or "our") governing your access to and use of <strong className="text-white">Beypointer.com</strong> and the <strong className="text-white">Beypointer</strong> application, including all related services, features, content, accounts, and virtual items (collectively, the "Service").
            </p>
            <p className="text-cyan-400 font-semibold">By accessing or using the Service, you agree to be bound by this Agreement. If you do not agree, do not use the Service.</p>
            
            <h3 className="text-white font-bold mt-4">1. Eligibility</h3>
            <p>You must meet the minimum age required by the laws of your country or have permission from a parent or legal guardian to use the Service. If you are under the age of majority, your parent or legal guardian is responsible for your use of the Service.</p>
            
            <h3 className="text-white font-bold mt-4">2. License, Not Ownership</h3>
            <p>All software, games, accounts, virtual items, virtual currency, and content made available through the Service are <strong className="text-cyan-400">licensed, not sold</strong>, to you.</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>You do <strong>not</strong> own any accounts, progress, virtual items, or virtual currency.</li>
              <li>You are granted a <strong>limited, revocable, non-exclusive, non-transferable license</strong> to access and use the Service for personal, non-commercial purposes.</li>
              <li>BBXD may revoke or terminate this license at any time in accordance with this Agreement.</li>
            </ul>
            
            <h3 className="text-white font-bold mt-4">3. Virtual Currency & Microtransactions</h3>
            <p>The Service may allow you to purchase or earn virtual currency, items, cosmetics, or upgrades ("Virtual Items").</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Virtual Items have <strong>no real-world monetary value</strong>.</li>
              <li>Virtual Items cannot be redeemed, transferred, or exchanged for real money or property.</li>
              <li>All purchases are <strong>final and non-refundable</strong>, except where required by law.</li>
              <li>Virtual Items may be lost due to updates, balance changes, resets, bugs, server shutdowns, inactivity, or account termination.</li>
              <li>Loss of Virtual Items does <strong>not</strong> entitle you to compensation or refunds.</li>
            </ul>
            
            <h3 className="text-white font-bold mt-4">4. Service Changes & Server Shutdowns</h3>
            <p>BBXD may, at any time and without prior notice:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Modify, update, suspend, or discontinue any part of the Service</li>
              <li>Shut down servers permanently or temporarily</li>
              <li>Remove or change game features, content, or functionality</li>
              <li>Reset rankings, progress, or Virtual Items</li>
            </ul>
            <p className="text-orange-400">You agree that BBXD is <strong>not responsible or liable</strong> for any resulting loss of access, progress, data, or Virtual Items.</p>
            
            <h3 className="text-white font-bold mt-4">5. Account Suspension & Termination</h3>
            <p>BBXD may suspend or terminate your account or access to the Service if you:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Violate this Agreement</li>
              <li>Exploit bugs or unintended mechanics</li>
              <li>Use cheats, bots, hacks, or unauthorized software</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Engage in fraudulent, illegal, or harmful behavior</li>
            </ul>
            <p>Upon suspension or termination:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Your license to use the Service ends immediately</li>
              <li>You lose access to all accounts, progress, and Virtual Items</li>
              <li>No refunds or compensation will be issued</li>
            </ul>
            
            <h3 className="text-white font-bold mt-4">6. Acceptable Use</h3>
            <p>You agree that you will not:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Reverse engineer, decompile, or modify the Service</li>
              <li>Circumvent security or access controls</li>
              <li>Use the Service for commercial purposes</li>
              <li>Sell, trade, or transfer accounts or Virtual Items</li>
              <li>Disrupt or interfere with the Service or its servers</li>
            </ul>
            
            <h3 className="text-white font-bold mt-4">7. Disclaimer of Warranties</h3>
            <p>The Service is provided <strong className="text-orange-400">"AS IS" and "AS AVAILABLE."</strong></p>
            <p>To the fullest extent permitted by law, BBXD disclaims all warranties, express or implied, including but not limited to:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Continuous availability or uptime</li>
              <li>Error-free or uninterrupted operation</li>
              <li>Preservation of data, progress, or Virtual Items</li>
              <li>Fitness for a particular purpose</li>
            </ul>
            
            <h3 className="text-white font-bold mt-4">8. Limitation of Liability</h3>
            <p>To the maximum extent permitted by applicable law:</p>
            <ul className="list-disc list-inside space-y-1 ml-2 text-orange-400">
              <li><strong>BBXD shall not be liable for any damages of any kind</strong>, including direct, indirect, incidental, consequential, special, exemplary, or punitive damages.</li>
              <li><strong>BBXD shall not be liable for any loss of Virtual Items, virtual currency, accounts, progress, data, access, or services</strong>, regardless of cause.</li>
              <li><strong>BBXD shall not be liable for any refunds, reimbursements, or compensation</strong>, including amounts paid for digital goods, subscriptions, or microtransactions.</li>
              <li><strong>BBXD's total liability for any claim arising out of or relating to the Service shall be zero dollars ($0).</strong></li>
            </ul>
            <p className="text-red-400 font-semibold">You acknowledge and agree that you use the Service entirely at your own risk.</p>
            
            <h3 className="text-white font-bold mt-4">9. Dispute Resolution & Arbitration</h3>
            <p>Any dispute, claim, or controversy arising out of or relating to this Agreement or the Service shall be resolved through <strong>binding arbitration</strong>, not in court.</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>You waive the right to a jury trial</li>
              <li>You waive the right to participate in class or collective actions</li>
              <li>Arbitration shall be conducted on an individual basis only</li>
            </ul>
            
            <h3 className="text-white font-bold mt-4">10. Changes to This Agreement</h3>
            <p>BBXD may revise this Agreement at any time. Continued use of the Service after changes are posted constitutes acceptance of the revised Agreement.</p>
            
            <h3 className="text-white font-bold mt-4">11. Governing Law</h3>
            <p>This Agreement shall be governed by and interpreted in accordance with applicable laws, without regard to conflict-of-law principles.</p>
            
            <h3 className="text-white font-bold mt-4">12. Contact Information</h3>
            <p>Questions regarding this Agreement may be directed through the official website:</p>
            <p><strong>Company:</strong> BBXD<br/><strong>Website:</strong> <a href="https://beypointer.com" className="text-cyan-400 hover:underline">https://beypointer.com</a></p>
            
            <div className="bg-slate-800 rounded-lg p-3 mt-4 border border-cyan-500/30">
              <p className="text-xs text-slate-400">✅ This User Agreement explicitly covers: Zero liability for microtransactions • Server shutdowns with no refunds • Licensed (not owned) digital items • Account termination without compensation • Arbitration & no class actions</p>
            </div>
          </div>
          <DialogFooter className="mt-4 pt-4 border-t border-slate-700 flex gap-2">
            <Button
              onClick={handleDeclineAgreement}
              disabled={loading}
              variant="outline"
              className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              Decline
            </Button>
            <Button
              onClick={handleAcceptAgreement}
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white font-bold"
            >
              {loading ? 'Creating Account...' : 'Accept & Continue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuthPage;
